<template>
  <v-container fluid class=" justify-start align-center " style="height: 100%">
    <h1>{{ $ml.get("reportsTitle") }}</h1>
    <v-row>
      <v-col cols="12" md="2">
        <v-dialog ref="dialog" v-model="dateModal" persistent width="290px">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="formattedDate"
              :label="$ml.get('reportsPeriodLabel')"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              clearable
              v-on="on"
            />
          </template>
          <v-date-picker v-model="searchDate" scrollable range>
            <v-spacer />
            <v-btn text color="primary" @click="dateModal = false">{{
              $ml.get("formCancelButton")
            }}</v-btn>
            <v-btn text color="primary" @click="closeModal()">{{
              $ml.get("formOkButton")
            }}</v-btn>
          </v-date-picker>
        </v-dialog>
      </v-col>
      <v-col cols="12" md="2" v-if="isSuperUser && showOrgFilter">
        <v-select
          v-model="selectedCompany"
          :items="companies"
          item-text="name"
          item-value="id"
          :label="$ml.get('reportsOrgLabel')"
          @change="changeCompany"
        />
      </v-col>
      <v-col cols="12" md="2">
        <v-select
          v-model="selectedTerminal"
          :items="terminals"
          item-text="serial_number"
          item-value="id"
          :label="$ml.get('reportsTermLabel')"
          @change="changeTerminal"
        />
      </v-col>
      <v-col cols="12" md="2" v-show="isOwner">
        <v-select
          v-model="selectedMember"
          :items="members"
          item-text="user.name"
          item-value="id"
          :label="$ml.get('reportsMemberLabel')"
          @change="changeMember"
        />
      </v-col>
      <v-col cols="12" md="2">
        <v-select
          v-model="selectedPaymentType"
          :items="paymentTypes"
          item-text="name"
          item-value="id"
          :label="$ml.get('reportsPayTypeLabel')"
          @change="changePaymentType"
        />
      </v-col>
      <v-col cols="12" md="2">
        <v-select
          v-model="selectedClient"
          :items="orgClients"
          item-text="name"
          item-value="id"
          :label="$ml.get('reportsClientLabel')"
          @change="changeClient"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="6">
        <PieChart
          :height="300"
          :chart-data="countData"
          :options="createOptions($ml.get('reportsCountTitle'))"
        />
      </v-col>
      <v-col cols="12" md="6">
        <PieChart
          style="height: 300px"
          :chart-data="paymentData"
          :options="createOptions($ml.get('reportsPaymentTitle'))"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <LineChart
          :height="300"
          :options="createOptions($ml.get('reportsDailyTitle'))"
          :chart-data="dailyData"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <LineChart
          :height="300"
          :options="createOptions($ml.get('reportsDailyCountTitle'))"
          :chart-data="dailyCountData"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <LineChart
          :height="300"
          :options="createOptions($ml.get('reportsHourTitle'))"
          :chart-data="hourData"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <h3>{{ $ml.get("reportsStatsTitle") }}</h3>
        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">
                  {{ $ml.get("reportsStatsHeaderService") }}
                </th>
                <th class="text-left">
                  {{ $ml.get("reportsStatsHeaderCount") }}
                </th>
                <th class="text-left">
                  {{ $ml.get("reportsStatsHeaderPercentPast") }}
                </th>
                <th class="text-left">
                  {{ $ml.get("reportsStatsHeaderCost") }}
                </th>
                <th class="text-left">
                  {{ $ml.get("reportsStatsHeaderPercentPast") }}
                </th>
                <th class="text-left">
                  {{ $ml.get("reportsStatsHeaderDiscount") }}
                </th>
                <th class="text-left">
                  {{ $ml.get("reportsStatsHeaderRevenue") }}
                </th>
                <th class="text-left">
                  {{ $ml.get("reportsStatsHeaderMiddle") }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in full_stats" :key="item.service_name">
                <td>
                  {{ item.service_name || $ml.get("reportsStatsManMsg") }}
                </td>
                <td>{{ item.totals.count }}</td>
                <td>
                  <span
                    :class="{
                      'success--text': item.count_percent > 0,
                      'error--text': item.count_percent < 0
                    }"
                    >{{ item.count_percent || "-"
                    }}<span v-if="item.count_percent">%</span></span
                  >
                </td>
                <td>{{ item.totals.cost | currency }}</td>
                <td>
                  <span
                    :class="{
                      'success--text': item.cost_percent > 0,
                      'error--text': item.cost_percent < 0
                    }"
                    >{{ item.cost_percent || "-"
                    }}<span v-if="item.cost_percent">%</span></span
                  >
                </td>
                <td>
                  {{ (item.totals.cost - item.totals.revenue) | currency }}
                </td>
                <td>{{ item.totals.revenue | currency }}</td>
                <td>
                  {{ (item.totals.revenue / item.totals.count) | currency }}
                </td>
              </tr>
              <tr class="font-weight-bold">
                <td>{{ $ml.get("reportsStatsTotal") }}</td>
                <td>{{ statsTotal.count }}</td>
                <td>
                  <span
                    :class="{
                      'success--text': statsTotal.count_percent > 0,
                      'error--text': statsTotal.count_percent < 0
                    }"
                    >{{ statsTotal.count_percent || "-"
                    }}<span v-if="statsTotal.count_percent">%</span></span
                  >
                </td>
                <td>{{ statsTotal.cost | currency }}</td>
                <td>
                  <span
                    :class="{
                      'success--text': statsTotal.cost_percent > 0,
                      'error--text': statsTotal.cost_percent < 0
                    }"
                    >{{ statsTotal.cost_percent || "-"
                    }}<span v-if="statsTotal.cost_percent">%</span></span
                  >
                </td>
                <td>{{ (statsTotal.cost - statsTotal.revenue) | currency }}</td>
                <td>{{ statsTotal.revenue | currency }}</td>
                <td>
                  {{
                    (statsTotal.count != 0
                      ? statsTotal.revenue / statsTotal.count
                      : 0) | currency
                  }}
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <div class="d-flex align-start">
          <h3>{{ $ml.get("reportsLogentryTitle") }}</h3>
          <div class="d-flex ml-auto">
            <v-btn color="primary" @click="download()">{{
              $ml.get("reportsLogentryDownloadBtn")
            }}</v-btn>
            <v-btn color="primary ml-5" @click="downloadClient()">{{
              $ml.get("reportsLogentryDownloadClientBtn")
            }}</v-btn>
          </div>
        </div>

        <v-simple-table fixed-header>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">
                  {{ $ml.get("reportsLogentryHeadService") }}
                </th>
                <th class="text-left">
                  {{ $ml.get("reportsLogentryHeadServiceType") }}
                </th>
                <th class="text-left">
                  {{ $ml.get("reportsLogentryHeadPrice") }}
                </th>
                <th class="text-left">
                  {{ $ml.get("reportsLogentryHeadDiscount") }}
                </th>
                <th class="text-left">
                  {{ $ml.get("reportsLogentryHeadAmount") }}
                </th>
                <th class="text-left">
                  {{ $ml.get("reportsLogentryHeadMode") }}
                </th>
                <th class="text-left">
                  {{ $ml.get("reportsLogentryHeadStart") }}
                  <br />
                  {{ $ml.get("reportsLogentryHeadEnd") }}
                </th>
                <th class="text-left">
                  {{ $ml.get("reportsLogentryHeadTime") }}
                </th>
                <th class="text-left">
                  {{ $ml.get("reportsLogentryHeadType") }}
                </th>
                <th class="text-left">
                  {{ $ml.get("reportsLogentryHeadState") }}
                </th>
                <th class="text-left">
                  {{ $ml.get("reportsLogentryHeadDate") }}
                </th>
                <th class="text-left">
                  {{ $ml.get("reportsLogentryHeadMember") }}
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in logentry"
                :key="item.id"
                @click="getDumps(item)"
                style="cursor: pointer"
              >
                <td>
                  <span v-if="item.service">{{
                    item.service.name || "-"
                  }}</span>
                </td>
                <td>
                  <span v-if="item.service">{{
                    item.service.is_wash ? "С мойкой" : "Без мойки"
                  }}</span>
                </td>
                <td>
                  <span v-if="item.service">{{
                    item.service.price | currency
                  }}</span>
                </td>
                <td>
                  <span v-if="item.payment">{{
                    (item.service.price - item.payment.amount) | currency
                  }}</span>
                  <span v-else-if="item.coupon && item.service">
                    {{ item.service.price | currency }}
                  </span>
                  <div v-if="item.coupon">
                    <span>{{ item.coupon }}</span>
                  </div>
                </td>
                <td>
                  <span v-if="item.payment">{{
                    item.payment.amount | currency
                  }}</span>
                  <span v-else-if="item.coupon">0.00 ₽</span>
                  <span v-else>{{ $ml.get("reportsLogentryNoPay") }}</span>
                </td>
                <td>
                  <span v-if="item.wash_mode"
                    >{{ $ml.get("reportsLogentryModeLabel") }}
                    {{ item.wash_mode }}</span
                  >
                </td>
                <td>
                  <div v-if="item.wash_start || item.wash_end">
                    <span>{{ item.wash_start_str || "-" }}</span>
                    <br />
                    <span>{{ item.wash_end_str || "-" }}</span>
                  </div>
                  <span v-else>-</span>
                </td>
                <td>{{ item.wash_time_str || "-" }}</td>
                <td>
                  {{
                    item.wash_started_manually
                      ? $ml.get("reportsLogentryWashManually")
                      : $ml.get("reportsLogentryWashAuto")
                  }}
                </td>
                <td>
                  {{
                    item.wash_end
                      ? $ml.get("reportsLogentryWashEnd")
                      : $ml.get("reportsLogentryWashNoEnd")
                  }}
                </td>
                <td>{{ item.created_at_str }}</td>
                <td>
                  <span>{{ item.member ? item.member.user.name : "-" }}</span>
                </td>
                <td>
                  <v-btn
                    v-if="
                      item.payment &&
                        item.payment.amount > 0 &&
                        (item.payment.status === 'success' ||
                          item.payment.type === 'cash')
                    "
                    text
                    icon
                    color="primary"
                    @click.stop="getReceipt(item)"
                  >
                    <v-icon>mdi-receipt</v-icon>
                  </v-btn>
                </td>
              </tr>
              <tr class="table-pagination">
                <td colspan="10">
                  <v-pagination
                    v-model="logentryOptions.page"
                    :length="logentryOptions.pageCount"
                    :total-visible="10"
                    @input="getLogentryInfo"
                  ></v-pagination>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-col>
    </v-row>
    <v-dialog
      v-model="dialogDumpInfo"
      :retain-focus="false"
      persistent
      max-width="1000px"
    >
      <v-card>
        <v-card-title>
          {{ $ml.get("reportsDumpTitle") }} {{ currentLogentry.created_at_str }}
        </v-card-title>
        <v-card-subtitle>
          {{
            $ml
              .with(0, currentLogentry.wash_start_str || "-")
              .with(1, currentLogentry.wash_end_str || "-")
              .with(2, currentLogentry.wash_time_str)
              .get("reportsDumpSubTitle")
          }}
        </v-card-subtitle>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <apexchart
                type="rangeBar"
                :options="chartOptions"
                :series="lodentryDump"
              ></apexchart>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" text @click="dialogDumpInfo = false">
            {{ $ml.get("modalCloseButton") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import orgApi from "@/modules/Organizations/api/";
import termApi from "@/modules/Terminals/api/";
import clientApi from "@/modules/Clients/api/";
import api from "@/api";
import _ from "lodash";
import PieChart from "./PieChart";
import moment from "moment";
import LineChart from "./LineChart";
import saveAs from "file-saver";
import ru from "apexcharts/dist/locales/ru.json";
import en from "apexcharts/dist/locales/en.json";
import localForage from "localforage";

export default {
  name: "Reports",
  components: {
    PieChart,
    LineChart
  },
  data() {
    return {
      searchDate: [
        moment()
          .startOf("month")
          .format("YYYY-MM-DD"),
        moment().format("YYYY-MM-DD")
      ],
      stats: {},
      full_stats: [],
      full_stats_past: [],
      statsTotal: { count: 0, cost: 0, revenue: 0 },
      statsTotalPast: { count: 0, cost: 0, revenue: 0 },
      countData: {},
      paymentData: {},
      dailyData: {},
      dailyCountData: {},
      hourData: {},
      dateModal: false,
      logentry: [],
      logentryOptions: {
        limit: 30,
        offset: 0,
        total: 0,
        page: 1,
        pageCount: 1
      },
      selectedCompany: null,
      selectedTerminal: null,
      selectedMember: null,
      selectedPaymentType: null,
      selectedClient: null,
      companies: [
        {
          id: null,
          name: this.$ml.get("reportsAllLabel")
        }
      ],
      terminals: [
        {
          id: null,
          name: this.$ml.get("reportsAllLabel")
        }
      ],
      members: [
        {
          id: null,
          name: this.$ml.get("reportsAllLabel")
        }
      ],
      paymentTypes: [
        { id: null, name: this.$ml.get("reportsAllLabel") },
        { id: 0, name: this.$ml.get("reportsPayTypeNoCouponLabel") },
        { id: 1, name: this.$ml.get("reportsPayTypeCouponLabel") }
      ],
      orgClients: [
        {
          id: null,
          name: this.$ml.get("reportsAllLabel")
        }
      ],
      dialogDumpInfo: false,
      lodentryDump: [],
      currentLogentry: {},
      chartOptions: {
        chart: {
          type: "rangeBar",
          locales: [ru, en],
          defaultLocale: this.$ml.get("chartLocale")
        },
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: "90%",
            rangeBarGroupRows: true
          }
        },
        xaxis: {
          type: "datetime",
          tickAmount: 10,
          labels: {
            datetimeUTC: false,
            showDuplicates: true,
            formatter: function(val, timestamp) {
              return moment(val).format("HH:mm");
            }
          }
        },
        stroke: {
          width: 1
        },
        fill: {
          type: "solid",
          opacity: 0.6
        },
        legend: {
          position: "top",
          horizontalAlign: "left"
        },
        tooltip: {
          x: {
            format: "HH:mm:ss"
          }
        }
      }
    };
  },
  mounted() {
    // Временный (постоянный) хак, для получения всех организаций для выбора,
    // чтобы не тратить время на реализацию перелистывания
    orgApi.getOrganizations({limit: 1000}).then(async response => {
      this.companies = response.data;
      if (this.companies.length == 1) {
        this.selectedCompany = this.companies[0].id;
      }
      if (this.searchedCompany) {
        this.selectedCompany = this.searchedCompany.id;
        if (!this.isOwner) {
          this.selectedMember =
            this.searchedCompany.member || this.currentUser.id;
        }
      }
      let filterObj = await localForage.getItem(
        `order_filter_${this.currentUser.id}`
      );
      if (filterObj) {
        if (_.has(filterObj, "company")) {
          if (_.find(this.companies, { id: filterObj["company"] })) {
            this.selectedCompany = filterObj["company"];
            if (_.has(filterObj, "terminal")) {
              this.selectedTerminal = filterObj["terminal"];
            }
            if (_.has(filterObj, "member")) {
              this.selectedMember = filterObj["member"];
            }
          }
        }
        if (_.has(filterObj, "paymentType")) {
          this.selectedPaymentType = filterObj["paymentType"];
        }
        if (_.has(filterObj, "searchDate")) {
          this.searchDate = filterObj["searchDate"];
        }
      }
      if (this.selectedCompany) {
        this.changeCompany();
      }
    });
  },
  watch: {
    searchedCompany(val) {
      this.selectedCompany = val.id;
      this.changeCompany();
    },
    $route() {
      if (this.searchedCompany) {
        this.selectedCompany = this.searchedCompany.id;
        this.changeCompany();
      }
    }
  },
  computed: {
    formattedDate() {
      return this.searchDate.join("~");
    },
    searchedCompany() {
      return this.$store.getters["navigation/selectedCompanyObject"];
    },
    isSuperUser() {
      return !!this.$store.getters["user/authUserInfo"]
        ? this.$store.getters["user/authUserInfo"].is_superuser
        : false;
    },
    showOrgFilter() {
      return this.$route.meta.orgFilter || false;
    },
    currentUser() {
      return this.$store.getters["user/authUserInfo"];
    },
    isOwner() {
      return (
        this.searchedCompany.owner &&
        this.currentUser &&
        this.searchedCompany.owner.id == this.currentUser.id
      );
    }
  },
  methods: {
    createOptions(title, displayLegend = true) {
      return {
        title: {
          display: true,
          text: title,
          fontSize: 24
        },
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: "bottom",
          display: displayLegend
        },
        plugins: {
          labels: {
            // render 'label', 'value', 'percentage', 'image' or custom function, default is 'percentage'
            render: "label"
          }
        }
      };
    },
    getLogentryInfo() {
      let api = orgApi;
      let searchId = this.selectedCompany;
      if (this.selectedTerminal) {
        api = termApi;
        searchId = this.selectedTerminal;
      }
      let start_date, end_date;
      if (this.searchDate.length === 2) {
        start_date = moment(this.searchDate[0])
          .startOf("day")
          .format("YYYY-MM-DDTHH:mmZ");
        end_date = moment(this.searchDate[1])
          .endOf("day")
          .format("YYYY-MM-DDTHH:mmZ");
      }
      api
        .getLogentry(searchId, {
          start_date,
          end_date,
          member: this.selectedMember,
          coupon: this.selectedPaymentType,
          client_id: this.selectedClient,
          offset: this.logentryOptions.limit * (this.logentryOptions.page - 1),
          limit: this.logentryOptions.limit
        })
        .then(response => {
          this.logentry = response.data;
          this.logentryOptions.total = response.headers["x-total-count"];
          if (this.logentryOptions.total > 0) {
            this.logentryOptions.pageCount = Math.ceil(
              this.logentryOptions.total / this.logentryOptions.limit
            );
          } else {
            this.logentryOptions.pageCount = 1;
          }
          _.forEach(this.logentry, item => {
            item.created_at_str = moment(item.created_at).format(
              this.$ml.get("dateTimeFormat")
            );
            item.updated_at_str = moment(item.updated_at).format(
              this.$ml.get("dateTimeFormat")
            );
            if (item.wash_start) {
              item.wash_start_str = moment(item.wash_start).format(
                this.$ml.get("dateTimeFormat")
              );
            }
            if (item.wash_end) {
              item.wash_end_str = moment(item.wash_end).format(
                this.$ml.get("dateTimeFormat")
              );
            }
            if (item.wash_start && item.wash_end) {
              item.wash_time = moment.duration(
                moment(item.wash_end).diff(moment(item.wash_start))
              );
              item.wash_time_str = `${(
                "0" + item.wash_time.get("minutes")
              ).slice(-2)}:${("0" + item.wash_time.get("seconds")).slice(-2)}`;
            }
          });
        });
    },
    changeCompany() {
      termApi.getOrganizationsTerminals(this.selectedCompany).then(response => {
        this.terminals = response.data;
        if (this.terminals.length === 1) {
          this.selectedTerminal = this.terminals[0].id;
        } else {
          if (
            this.selectedTerminal &&
            !_.find(this.terminals, { id: this.selectedTerminal })
          ) {
            this.selectedTerminal = null;
          }
          this.terminals.unshift({
            id: null,
            serial_number: this.$ml.get("reportsAllLabel")
          });
        }
        this.fillData();
        this.logentryOptions.page = 1;
        setTimeout(this.getLogentryInfo, 1500);
      });
      orgApi
        .getOrganizationsMembers(this.selectedCompany, { is_active: true })
        .then(response => {
          this.members = response.data;
          this.members.unshift({
            id: null,
            user: { name: this.$ml.get("reportsAllLabel") }
          });
          // this.fillData();
          // this.logentryOptions.page = 1;
          // this.getLogentryInfo();
          clientApi
            .getOrganizationClients(this.selectedCompany, { has_coupon: true })
            .then(response => {
              this.orgClients = response.data;
              this.orgClients.unshift({
                id: null,
                name: this.$ml.get("reportsAllLabel")
              });
            });
        });
    },
    changeTerminal() {
      this.fillData();
      this.logentryOptions.page = 1;
      this.getLogentryInfo();
    },
    changeMember() {
      this.fillData();
      this.logentryOptions.page = 1;
      this.getLogentryInfo();
    },
    changePaymentType() {
      this.fillData();
      this.logentryOptions.page = 1;
      this.getLogentryInfo();
    },
    changeClient() {
      this.fillData();
      this.logentryOptions.page = 1;
      this.getLogentryInfo();
    },
    closeModal() {
      this.dateModal = false;
      if (this.searchDate.length === 1) {
        this.searchDate.push(this.searchDate[0]);
      }
      this.fillData();
      this.logentryOptions.page = 1;
      this.getLogentryInfo();
    },
    fillData() {
      this.saveFilter();
      let api = orgApi;
      let searchId = this.selectedCompany;
      if (this.selectedTerminal) {
        api = termApi;
        searchId = this.selectedTerminal;
      }
      let since, until;
      if (this.searchDate.length === 2) {
        since = moment(this.searchDate[0])
          .startOf("day")
          .format("YYYY-MM-DDTHH:mmZ");
        until = moment(this.searchDate[1])
          .endOf("day")
          .format("YYYY-MM-DDTHH:mmZ");
      }
      api
        .getStats(searchId, {
          since,
          until,
          member: this.selectedMember,
          coupon: this.selectedPaymentType,
          client_id: this.selectedClient
        })
        .then(response => {
          this.stats = response.data[0] || {};
          this.full_stats = this.stats.full_service || [];

          //full_service
          this.countData = {
            labels: [],
            datasets: [
              {
                data: [],
                backgroundColor: [
                  "#ef727a",
                  "#3598db",
                  "#e8d349",
                  "#c45850",
                  "#6dcab5",
                  "#ad1ab5"
                ]
              }
            ]
          };
          this.paymentData = {
            labels: [],
            datasets: [
              {
                data: [],
                backgroundColor: [
                  "#ef727a",
                  "#3598db",
                  "#e8d349",
                  "#c45850",
                  "#6dcab5",
                  "#ad1ab5"
                ]
              }
            ]
          };
          this.statsTotal = {
            count: 0,
            cost: 0,
            revenue: 0
          };
          _.forEach(this.full_stats, stat => {
            this.countData.labels.push(
              `${stat.service_name || this.$ml.get("reportServiceManually")} (${
                stat.totals.count
              })`
            );
            this.countData.datasets[0].data.push(stat.totals.count);
            this.paymentData.labels.push(
              `${stat.service_name ||
                this.$ml.get("reportServiceManually")} - ${stat.totals.revenue}`
            );
            this.paymentData.datasets[0].data.push(stat.totals.revenue);
            if (stat.service_name) {
              this.statsTotal.count += stat.totals.count;
              this.statsTotal.cost += stat.totals.cost;
              this.statsTotal.revenue += stat.totals.revenue;
            }
          });

          //revenue_by_days
          let daily = {
            labels: [],
            datasets: [
              {
                label: `${this.$ml.get("reportsDailyTitle")} ${moment(
                  this.searchDate[0]
                ).format("YYYY")}`,
                borderColor: "#5face4",
                fill: false,
                pointRadius: 5,
                data: []
              }
            ]
          };
          _.forEach(
            Array.from(moment.range(this.searchDate).by("day")),
            date => {
              let strDate = date.format("DD.MM");
              daily.labels.push(strDate);
              let s = 0;
              _.forEach(this.stats.revenue_by_days || [], item => {
                if (moment.unix(item[0]).format("DD.MM") == strDate) {
                  s = item[1];
                }
              });
              daily.datasets[0].data.push(s);
            }
          );

          //count_by_days
          let dailyCount = {
            labels: [],
            datasets: [
              {
                label: `${this.$ml.get("reportsStatsHeaderCount")} ${moment(
                  this.searchDate[0]
                ).format("YYYY")}`,
                borderColor: "#5face4",
                fill: false,
                pointRadius: 5,
                data: []
              }
            ]
          };
          _.forEach(
            Array.from(moment.range(this.searchDate).by("day")),
            date => {
              let strDate = date.format("DD.MM");
              dailyCount.labels.push(strDate);
              let s = 0;
              _.forEach(this.stats.count_by_days || [], item => {
                if (moment.unix(item[0]).format("DD.MM") == strDate) {
                  s = item[1];
                }
              });
              dailyCount.datasets[0].data.push(s);
            }
          );

          //past_data
          let since_past, until_past;
          if (this.searchDate.length === 2) {
            since_past = moment(this.searchDate[0])
              .startOf("day")
              .subtract(1, "years")
              .format("YYYY-MM-DDTHH:mmZ");
            until_past = moment(this.searchDate[1])
              .endOf("day")
              .subtract(1, "years")
              .format("YYYY-MM-DDTHH:mmZ");
          }
          if (this.searchDate.length === 2) {
            api
              .getStats(searchId, {
                since: since_past,
                until: until_past,
                member: this.selectedMember,
                coupon: this.selectedPaymentType,
                client_id: this.selectedClient
              })
              .then(response => {
                //full_stats_percent
                let stat = response.data[0] || [];
                this.full_stats_past = stat.full_service || [];
                this.statsTotalPast = {
                  count: 0,
                  cost: 0,
                  revenue: 0
                };
                _.forEach(this.full_stats_past, stat => {
                  let stat_index = _.findIndex(this.full_stats, s => {
                    return _.trim(s.service_name) == _.trim(stat.service_name);
                  });
                  if (stat_index > -1) {
                    let cur = this.full_stats[stat_index].totals;
                    this.$set(
                      this.full_stats[stat_index],
                      "count_percent",
                      _.round(
                        ((cur.count - stat.totals.count) / stat.totals.count) *
                          100,
                        2
                      )
                    );
                    this.$set(
                      this.full_stats[stat_index],
                      "cost_percent",
                      _.round(
                        ((cur.cost - stat.totals.cost) / stat.totals.cost) *
                          100,
                        2
                      )
                    );
                  }
                  if (stat.service_name) {
                    this.statsTotalPast.count += stat.totals.count;
                    this.statsTotalPast.cost += stat.totals.cost;
                    this.statsTotalPast.revenue += stat.totals.revenue;
                  }
                });
                if (this.statsTotalPast.count > 0) {
                  this.$set(
                    this.statsTotal,
                    "count_percent",
                    _.round(
                      ((this.statsTotal.count - this.statsTotalPast.count) /
                        this.statsTotalPast.count) *
                        100,
                      2
                    )
                  );
                  this.$set(
                    this.statsTotal,
                    "cost_percent",
                    _.round(
                      ((this.statsTotal.cost - this.statsTotalPast.cost) /
                        this.statsTotalPast.cost) *
                        100,
                      2
                    )
                  );
                }

                //revenue_by_days
                daily.datasets.push({
                  label: `${this.$ml.get("reportsDailyTitle")} ${moment(
                    since_past
                  ).format("YYYY")}`,
                  borderColor: "#e45fac",
                  fill: false,
                  pointRadius: 5,
                  data: []
                });
                _.forEach(
                  Array.from(moment.range([since_past, until_past]).by("day")),
                  date => {
                    let strDate = date.format("DD.MM");
                    let s = 0;
                    _.forEach(stat.revenue_by_days, item => {
                      if (moment.unix(item[0]).format("DD.MM") == strDate) {
                        s = item[1];
                      }
                    });
                    daily.datasets[1].data.push(s);
                  }
                );
                this.dailyData = daily;

                //count_by_days
                dailyCount.datasets.push({
                  label: `${this.$ml.get("reportsStatsHeaderCount")} ${moment(
                    since_past
                  ).format("YYYY")}`,
                  borderColor: "#e45fac",
                  fill: false,
                  pointRadius: 5,
                  data: []
                });
                _.forEach(
                  Array.from(moment.range([since_past, until_past]).by("day")),
                  date => {
                    let strDate = date.format("DD.MM");
                    let s = 0;
                    _.forEach(stat.count_by_days, item => {
                      if (moment.unix(item[0]).format("DD.MM") == strDate) {
                        s = item[1];
                      }
                    });
                    dailyCount.datasets[1].data.push(s);
                  }
                );
                this.dailyCountData = dailyCount;
              });
          } else {
            this.dailyData = daily;
            this.dailyCountData = dailyCount;
          }

          //daily_load_by_hours
          let firstData = this.stats.daily_load_by_hours || [];
          let since2, until2;
          if (since) {
            since2 = moment(since)
              .subtract(1, "months")
              .startOf("day")
              .format("YYYY-MM-DDTHH:mmZ");
          }
          if (until) {
            until2 = moment(until)
              .subtract(1, "months")
              .endOf("day")
              .format("YYYY-MM-DDTHH:mmZ");
          }
          api
            .getStats(searchId, {
              stat: "daily_load_by_hours",
              since: since2,
              until: until2,
              member: this.selectedMember,
              coupon: this.selectedPaymentType,
              client_id: this.selectedClient
            })
            .then(response => {
              this.hourData = {
                // eslint-disable-next-line prettier/prettier
                labels: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23],
                datasets: [
                  {
                    label: this.$ml
                      .with(0, moment(since).format("DD.MM.YY"))
                      .with(1, moment(until).format("DD.MM.YY"))
                      .get("reportsHourDatasetLabel"),
                    borderColor: "#5face4",
                    fill: false,
                    pointRadius: 5,
                    data: []
                  },
                  {
                    label: this.$ml
                      .with(0, moment(since2).format("DD.MM.YY"))
                      .with(1, moment(until2).format("DD.MM.YY"))
                      .get("reportsHourDatasetLabel"),
                    borderColor: "#e45fac",
                    fill: false,
                    pointRadius: 5,
                    data: []
                  }
                ]
              };
              _.forEach(this.hourData.labels, label => {
                let count = 0,
                  count2 = 0;
                _.forEach(firstData, item => {
                  if (item[0] === label) {
                    count = item[1];
                  }
                });
                _.forEach(response.data, item => {
                  if (item[0] === label) {
                    count2 = item[1];
                  }
                });
                this.hourData.datasets[0].data.push(count);
                this.hourData.datasets[1].data.push(count2);
              });
            });
        });
    },
    download() {
      let api = orgApi;
      let searchId = this.selectedCompany;
      if (this.selectedTerminal) {
        api = termApi;
        searchId = this.selectedTerminal;
      }
      let start_date, end_date;
      if (this.searchDate.length === 2) {
        start_date = moment(this.searchDate[0])
          .startOf("day")
          .format("YYYY-MM-DDTHH:mmZ");
        end_date = moment(this.searchDate[1])
          .endOf("day")
          .format("YYYY-MM-DDTHH:mmZ");
      }
      api
        .getLogentryXmlReport(searchId, {
          responseType: "blob",
          params: {
            start_date,
            end_date,
            member: this.selectedMember,
            coupon: this.selectedPaymentType,
            client_id: this.selectedClient
          }
        })
        .then(
          res => {
            var blob = new Blob([res.data], {
              type: "application/force-download;"
            });
            let filename = "";
            let disposition = res.headers["content-disposition"];
            if (disposition && disposition.indexOf("attachment") !== -1) {
              let filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
              let matches = filenameRegex.exec(disposition);
              if (matches != null && matches[1]) {
                filename = matches[1].replace(/['"]/g, "");
                filename = filename.replace("utf-8", "");
              }
            }
            saveAs(blob, decodeURI(filename));
          },
          () => {
            this.$toast.error(this.$ml.get("reportsDownloadErrorMsg"));
          }
        );
    },
    downloadClient() {
      let api = orgApi;
      let searchId = this.selectedCompany;
      if (this.selectedTerminal) {
        api = termApi;
        searchId = this.selectedTerminal;
      }
      let start_date, end_date;
      if (this.searchDate.length === 2) {
        start_date = moment(this.searchDate[0])
          .startOf("day")
          .format("YYYY-MM-DDTHH:mmZ");
        end_date = moment(this.searchDate[1])
          .endOf("day")
          .format("YYYY-MM-DDTHH:mmZ");
      }
      api
        .getLogentryXmlClientReport(searchId, {
          responseType: "blob",
          params: {
            start_date,
            end_date,
            member: this.selectedMember,
            coupon: this.selectedPaymentType,
            client_id: this.selectedClient
          }
        })
        .then(
          res => {
            var blob = new Blob([res.data], {
              type: "application/force-download;"
            });
            let filename = "";
            let disposition = res.headers["content-disposition"];
            if (disposition && disposition.indexOf("attachment") !== -1) {
              let filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
              let matches = filenameRegex.exec(disposition);
              if (matches != null && matches[1]) {
                filename = matches[1].replace(/['"]/g, "");
                filename = filename.replace("utf-8", "");
              }
            }
            saveAs(blob, decodeURI(filename));
          },
          () => {
            this.$toast.error(this.$ml.get("reportsDownloadErrorMsg"));
          }
        );
    },
    getDumps(item) {
      api.get(`/logentries/${item.id}/dump-detail`).then(response => {
        this.currentLogentry = item;
        _.forEach(response.data.in_info, item => {
          _.forEach(item.data, data => {
            _.forEach(data.y, (y, index) => {
              data.y[index] = new Date(moment(y * 1000)).getTime();
            });
          });
        });
        _.forEach(response.data.out_info, item => {
          _.forEach(item.data, data => {
            _.forEach(data.y, (y, index) => {
              data.y[index] = new Date(moment(y * 1000)).getTime();
            });
          });
        });
        let dumpData = response.data.in_info;
        _.forEach(response.data.out_info, data => {
          let item = _.find(dumpData, { name: data.name });
          if (item) {
            item.data = _.concat(item.data, data.data);
          }
        });
        if (item.terminal && item.wash_start && item.wash_end) {
          let params = {};
          params["start_date"] = item.wash_start;
          params["end_date"] = item.wash_end;
          api
            .get(`/terminals/${item.terminal.id}/messages`, {params: params})
            .then(response => {
              _.forEach(response.data, data => {
                let message_name = data.service_name || "Режим не установлен";
                let data_y = [
                  new Date(data.date_created).getTime(),
                  new Date(moment(data.date_created).add(10, 'seconds')).getTime()
                ];
                let item = _.find(dumpData, { name: message_name });
                if (item) {
                  item.data = _.concat(item.data, {x: data.message, y: data_y});
                }
              });
              this.lodentryDump = dumpData;
              this.dialogDumpInfo = true;
            });
        } else {
          this.lodentryDump = dumpData;
          this.dialogDumpInfo = true;
        }
      });
    },
    getReceipt(item) {
      api
        .get(`/logentries/${item.id}/receipt-url`)
        .then(response => {
          window.open(response.data, "_blank");
        })
        .catch(error => {
          const message = _.get(error, "response.data.detail");
          if (message) {
            this.$message.error(message);
          }
        });
    },
    saveFilter() {
      let filterObj = {
        searchDate: this.searchDate
      };
      if (this.selectedCompany) {
        filterObj["company"] = this.selectedCompany;
      }
      if (this.selectedTerminal) {
        filterObj["terminal"] = this.selectedTerminal;
      }
      if (this.selectedMember) {
        filterObj["member"] = this.selectedMember;
      }
      if (this.selectedPaymentType != null) {
        filterObj["paymentType"] = this.selectedPaymentType;
      }
      localForage.setItem(`order_filter_${this.currentUser.id}`, filterObj);
    }
  }
};
</script>

<style scoped>
.table-pagination:hover {
  background: transparent !important;
}
</style>
